// What a family motion shows in the hands for one exercise: the exercise's own equipment (or its demoProps), held the
// family's way. Pure, so the viewer, the label and the clip build's checks agree.
import { DEMO_FAMILIES, demoFamilyOf, demoPropsOf, type DemoHold, type DemoPropKind, type Equipment, type Exercise } from '@fitness/core';

export interface FamilyProps {
  /** Equipment shown, or null for empty hands. */
  kind: DemoPropKind | null;
  hold: DemoHold | null;
  /** Only the right arm moves; the left one rests. */
  oneArm: boolean;
  /** Playback speed. */
  tempo: number;
  /** A weight in the middle rests straight down (hip thrust, sit-up). */
  hangDown?: boolean;
}

const FROM_EQUIPMENT: Partial<Record<Equipment, DemoPropKind>> = {
  kettlebell: 'kettlebell', dumbbell: 'dumbbell', barbell: 'barbell', cable: 'bar', machine: 'bar', pullup_bar: 'bar', medicine_ball: 'ball',
};

/** Things that need two hands on one bar or ball. */
const TWO_HANDED = new Set<DemoPropKind>(['barbell', 'bar', 'ball']);

export type FamilyPropsInput = Pick<Exercise, 'id' | 'equipment' | 'demoFamily' | 'demoProps' | 'copiedFrom' | 'pattern' | 'category'>;

export function familyPropsFor(e: FamilyPropsInput): FamilyProps {
  const id = demoFamilyOf(e);
  const props = demoPropsOf(e);
  const tempo = typeof props.tempo === 'number' && Number.isFinite(props.tempo) ? Math.min(2, Math.max(0.5, props.tempo)) : 1;
  const oneArm = props.oneArm === true;
  if (!id) return { kind: null, hold: null, oneArm, tempo };
  const family = DEMO_FAMILIES[id];
  const chosen = props.equipment ?? FROM_EQUIPMENT[e.equipment] ?? 'none';
  let hold = props.hold ?? family.hold;
  if (chosen === 'none' || !hold) return { kind: null, hold: null, oneArm, tempo };
  // A bar or a ball needs both hands: on a one-hand motion it is left out, on a two-hand motion it goes between them.
  if (TWO_HANDED.has(chosen)) {
    if (hold === 'L' || hold === 'R' || oneArm) return { kind: null, hold: null, oneArm, tempo };
    hold = 'C';
  }
  return { kind: chosen, hold: oneArm ? 'R' : hold, oneArm, tempo, ...(family.hangDown ? { hangDown: true } : {}) };
}

const NAMES: Record<DemoPropKind, [string, string]> = {
  kettlebell: ['a kettlebell', 'two kettlebells'],
  dumbbell: ['a dumbbell', 'dumbbells'],
  barbell: ['a barbell', 'a barbell'],
  bar: ['a bar', 'a bar'],
  ball: ['a medicine ball', 'a medicine ball'],
};

/** "with dumbbells", "with a kettlebell", or "" for empty hands. */
export function propsLabel(p: FamilyProps): string {
  if (!p.kind) return '';
  return `with ${NAMES[p.kind][p.hold === 'LR' ? 1 : 0]}`;
}
